// Global scroll-reveal initializer.
// Walks every element with the `.reveal` class and toggles `.is-visible`
// once it enters the viewport. One-way: once revealed, stays revealed.
//
// On initial render: elements already in the viewport get a sequential
// `--reveal-section-i` index based on document order, so they cascade
// top-to-bottom on page load (gradient sweep effect). Elements scrolled
// into view later fire immediately with no per-section delay.
//
// Honors prefers-reduced-motion (immediately reveals everything, no observer).

let observer = null;

export const initReveal = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const targets = document.querySelectorAll(".reveal:not(.is-visible)");
  if (!targets.length) return;

  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion || typeof IntersectionObserver === "undefined") {
    targets.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  // Determine which targets are already in the viewport at init time.
  // Assign each one a sequential cascade index in document order so that
  // CSS can apply transition-delay = i * step.
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  const aboveFold = [];
  let cascadeIndex = 0;
  targets.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.top < viewportH && rect.bottom > 0) {
      el.style.setProperty("--reveal-section-i", String(cascadeIndex));
      cascadeIndex += 1;
      aboveFold.push(el);
    }
  });

  // Reveal the above-the-fold batch on the next frame so the CSS variable
  // is read before the .is-visible class triggers the transition.
  if (aboveFold.length) {
    window.requestAnimationFrame(() => {
      aboveFold.forEach((el) => el.classList.add("is-visible"));
    });
  }

  // For elements still off-screen, observe normally — each fires when it
  // enters the viewport, with no cumulative cascade delay.
  const aboveSet = new Set(aboveFold);
  const remaining = Array.from(targets).filter((el) => !aboveSet.has(el));
  if (!remaining.length) return;

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
  }

  remaining.forEach((el) => observer.observe(el));
};

export default initReveal;
